<template>
  <div class="mr-2 d-inline-flex">
    <b-tooltip
      :id="'tooltip-' + helpId.replace(/\s+/g, '-')"
      :target="'help-' + helpId.replace(/\s+/g, '-')"
      triggers="hover focus"
      custom-class="custom-tooltip"
      :aria-label="`Help for ${label}`"
      role="tooltip"
      @show="onTooltipShow"
      @hide="onTooltipHide"
    >
      <div
        ref="tooltipContent"
        v-html="helpText"
        tabindex="-1"
        @keydown="onTooltipKeydown"
      ></div>
    </b-tooltip>
    <b-icon
      class="ml-2 help-icon"
      :id="'help-' + helpId.replace(/\s+/g, '-')"
      icon="question-circle-fill"
      tabindex="0"
      :aria-label="`Help information for ${label}`"
      :aria-describedby="'tooltip-' + helpId.replace(/\s+/g, '-')"
      role="button"
      @keydown="onTriggerKeydown"
      @focus="onTriggerFocus"
      @blur="onTriggerBlur"
    ></b-icon>
    <!-- Visually hidden description for accessibility -->
    <div
      :id="'tooltip-' + helpId.replace(/\s+/g, '-')"
      class="sr-only"
      v-html="helpTextAria ? helpTextAria : helpText"
      aria-hidden="true"
    ></div>
  </div>
</template>

<script>
export default {
  name: "Tooltip",
  props: {
    helpText: {
      type: String,
      required: true,
    },
    helpTextAria: {
      type: String,
      required: false,
    },
    helpId: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      required: true,
    },
  },
  methods: {
    onTooltipKeydown(event) {
      if (event.key === "Escape") {
        event.preventDefault();
        const targetId = "help-" + this.helpId.replace(/\s+/g, "-");
        this.$root.$emit("bv::hide::tooltip", targetId);
        document.getElementById(targetId).focus();
      }
    },
    onTooltipShow() {
      // Tooltip is now visible
      this.$emit("tooltip-show");
    },
    onTooltipHide() {
      // Tooltip is now hidden
      this.$emit("tooltip-hide");
    },
    onTriggerKeydown(event) {
      // Handle ESC key to dismiss tooltip
      if (event.key === "Escape") {
        event.preventDefault();
        // console.log("Escape key pressed");
        const targetId = event.target.id;
        this.$root.$emit("bv::hide::tooltip", targetId);
        // Keep focus on the trigger element
        event.target.focus();
        this.$emit("tooltip-dismissed", { method: "escape" });
      }
    },
    onTriggerFocus(event) {
      // Ensure tooltip shows on focus
      const targetId = event.target.id;
      this.$root.$emit("bv::show::tooltip", targetId);
      this.$emit("tooltip-focused");
    },
    onTriggerBlur(event) {
      // Handle blur if needed - tooltip will hide automatically due to triggers
      this.$emit("tooltip-blurred");
    },
  },
};
</script>

<style scoped lang="scss">
// Tooltip accessibility styles
.custom-tooltip {
  max-width: 300px;
  max-height: 200px;

  .tooltip-inner {
    text-align: left;
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    max-height: 180px;
  }

  // Ensure tooltip content is focusable and keyboard navigable
  .overflow-div {
    outline: none;

    &:focus {
      outline: 2px solid #007bff;
      outline-offset: 2px;
    }
  }
}

// Ensure help icons have proper focus styling
.help-icon {
  outline: none;

  &:focus {
    outline: 2px solid #007bff;
    outline-offset: 2px;
    border-radius: 2px;
  }

  &:hover {
    cursor: pointer;
  }
}

.overflow-div {
  padding-right: 10px;
}
</style>
